import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { AppState } from 'src/state/store'

export enum FintwinStepEnum {
  IdentityVerification = 1,
  Address = 2,
  AboutYou = 3,
  AccountUse = 4,
  SourceOfFunds = 5,
  // Optional supporting-documents upload, sits just before the final
  // declarations step. Declarations shifted 6 -> 7 to make room.
  Documents = 6,
  Declarations = 7,
  Completed = 10,
}

// Lightweight reference to the online identity-verification session the user is
// going through. Provider-agnostic: the backend abstracts the actual provider.
export interface VerificationSession {
  sessionId: string
  status: string
}

export interface FintwinVerificationState {
  currentStep: FintwinStepEnum
  availableStep: FintwinStepEnum
  isCompleted: boolean
  formValues: Record<string, unknown>
  verificationSession: VerificationSession | null
  // Id of the user this in-progress questionnaire belongs to. Logout no longer
  // wipes the slice (so the same user can resume after logging back in), so the
  // owner is what guards against a different user on the same browser inheriting
  // these answers. See `claimFintwinOwner`.
  ownerId: number | null
}

export const initialFintwinVerificationState: FintwinVerificationState = {
  currentStep: FintwinStepEnum.IdentityVerification,
  availableStep: FintwinStepEnum.IdentityVerification,
  isCompleted: false,
  formValues: {},
  verificationSession: null,
  ownerId: null,
}

const hydrate = createAction<AppState>(HYDRATE)

const fintwinVerificationSlice = createSlice({
  name: 'fintwinVerification',
  initialState: initialFintwinVerificationState,
  reducers: {
    changeCurrentStep(state, action: PayloadAction<FintwinStepEnum>) {
      state.currentStep = action.payload
    },
    changeAvailableStep(state, action: PayloadAction<FintwinStepEnum>) {
      state.availableStep = action.payload
    },
    progressToStep(state, action: PayloadAction<FintwinStepEnum>) {
      const nextStep = action.payload

      state.currentStep = nextStep

      if (nextStep > state.availableStep) {
        state.availableStep = nextStep
      }
    },
    addFormValues(state, action: PayloadAction<Record<string, unknown>>) {
      state.formValues = { ...state.formValues, ...action.payload }
    },
    setVerificationSession(
      state,
      action: PayloadAction<VerificationSession | null>
    ) {
      state.verificationSession = action.payload
    },
    completeFintwinVerification(state) {
      state.isCompleted = true
      state.currentStep = FintwinStepEnum.Completed
      state.availableStep = FintwinStepEnum.Completed
    },
    // Claim the slice for the logged-in user on every login. The same owner
    // keeps their in-progress answers (resume after re-login); a different or
    // not-yet-known owner gets a clean questionnaire, so one user never inherits
    // another user's KYC answers on a shared browser.
    claimFintwinOwner(state, action: PayloadAction<number>) {
      if (state.ownerId !== action.payload) {
        return { ...initialFintwinVerificationState, ownerId: action.payload }
      }
    },
    flushFintwin() {
      return initialFintwinVerificationState
    },
  },
  extraReducers: builder => {
    builder.addCase(hydrate, state => state)
  },
})

const { actions, reducer } = fintwinVerificationSlice

export const {
  changeCurrentStep,
  changeAvailableStep,
  progressToStep,
  addFormValues,
  setVerificationSession,
  completeFintwinVerification,
  claimFintwinOwner,
  flushFintwin,
} = actions

export default reducer
